import React, { useRef, useState } from 'react';
import SignaturePad from 'react-signature-canvas';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const GpaFormToPDF = ({ declaration }) => {

    const today = new Date().toISOString().split('T')[0];
    const [formData, setFormData] = useState({
        entreprise: '',
        travaux: '',
        maitreOuvrage: '',
        client: '',
        dateReception: '',
        dateFait: today,
        decision: 'acceptée',
        delai: '',
        ville: '',
    });

    const [reserves, setReserves] = useState([{ text: '', files: [] }]);
    const signatureClientRef = useRef();
    const signatureEntrepriseRef = useRef();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleReserveChange = (index, value) => {
        const updated = [...reserves];
        updated[index].text = value;
        setReserves(updated);
    };

    const handleFileUpload = (index, files) => {
        const updated = [...reserves];
        updated[index].files = Array.from(files);
        setReserves(updated);
    };

    const addReserve = () => {
        setReserves([...reserves, { text: '', files: [] }]);
    };

    const removeReserve = (index) => {
        setReserves(reserves.filter((_, i) => i !== index));
    };

    const generatePdf = async () => {
        const signatureClient = signatureClientRef.current?.getCanvas().toDataURL('image/png') || '';
        const signatureEntreprise = signatureEntrepriseRef.current?.getCanvas().toDataURL('image/png') || '';

        const decisionText = {
            acceptée: `La réception des travaux est prononcée sans réserves avec effet à la date du ${formData.dateReception}.`,
            réservée: `La réception des travaux est prononcée avec les réserves consignées dans l’annexe ci-jointe à la date du ${formData.dateReception}.`,
            refusée: `La réception des travaux est refusée pour les motifs consignés dans l’annexe ci-jointe à la date du ${formData.dateReception}.`,
        };

        const content = [
            { text: 'PROCÈS-VERBAL DE RÉCEPTION DE TRAVAUX', style: 'title' },
            {
                table: {
                    widths: ['auto', '*'],
                    body: [
                        ['Entreprise', formData.entreprise],
                        ['Adresse', `${declaration.Adresse} ${declaration.Ville} ${declaration.Pays}`],
                        ['Travaux', formData.travaux],
                        ['Maître d\'ouvrage', formData.maitreOuvrage],
                        ['Client', formData.client],
                        ['Date de réception', formData.dateReception],
                    ]
                },
                layout: 'lightHorizontalLines',
                margin: [0, 20, 0, 20]
            },
            {
                text: 'Décision',
                style: 'sectionHeader'
            },
            {
                text: decisionText[formData.decision],
                margin: [0, 10, 0, 10]
            },
            {
                text: `Fait à ${formData.ville}, le ${formData.dateFait}`,
                margin: [0, 20, 0, 0]
            },
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            { text: "Signature de l'entreprise", style: 'signatureLabel' },
                            signatureEntreprise && { image: signatureEntreprise, width: 150, alignment: 'left', margin: [0, 10, 0, 0] },
                        ]
                    },
                    {
                        width: '*',
                        stack: [
                            { text: "Signature du client", style: 'signatureLabel' },
                            signatureClient && { image: signatureClient, width: 150, alignment: 'left', margin: [0, 10, 0, 0] },
                        ]
                    }
                ],
                columnGap: 40,
                margin: [0, 30, 0, 0]
            }
        ];

        if (formData.decision !== 'acceptée') {
            content.push(
                { text: '', pageBreak: 'before' },
                { text: 'ANNEXE - LISTE DES RÉSERVES', style: 'subheader' }
            );

            const fileImagePromises = [];

            reserves.forEach((reserve, index) => {
                content.push({
                    text: `${index + 1}. ${reserve.text || '(Réserve non renseignée)'}`,
                    margin: [0, 10, 0, 5]
                });

                reserve.files.forEach((file) => {
                    if (file.type.startsWith('image/')) {
                        const promise = new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = () => {
                                resolve({ image: reader.result, width: 250, margin: [0, 10, 0, 10] });
                            };
                            reader.readAsDataURL(file);
                        });
                        fileImagePromises.push(promise);
                    }
                });
            });

            const imageObjects = await Promise.all(fileImagePromises);
            content.push(...imageObjects);
        }

        pdfMake.createPdf({
            content,
            pageSize: 'A4',
            pageMargins: [40, 60, 40, 60],
            styles: {
                title: {
                    fontSize: 20,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 20]
                },
                subheader: {
                    fontSize: 16,
                    bold: true,
                    color: '#333333',
                    margin: [0, 0, 0, 10]
                },
                sectionHeader: {
                    fontSize: 14,
                    bold: true,
                    decoration: 'underline',
                    margin: [0, 20, 0, 5]
                },
                signatureLabel: {
                    fontSize: 12,
                    bold: true
                }
            }
        }).open();
    };

    // console.log(declaration)

    return (
        <div className="bg-gray-200 py-10">
            <button onClick={generatePdf} className="bg-orange-600 hover:bg-orange-700 mt-8 px-4 py-2 rounded text-white">
                Générer le PDF
            </button>
            <div className="bg-white shadow-md mx-auto px-20 py-24 w-full max-w-[1000px]">
                <h2 className="mb-8 font-semibold text-2xl text-center">
                    Procès-verbal de réception de travaux
                </h2>

                <div class="my-10 border-t"></div>

                <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">Entreprise :</span>
                        <input name="entreprise" placeholder="Société" value={formData.entreprise} onChange={handleChange} className="p-2 border rounded w-64" />
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">{declaration.Pays}</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">Travaux :</span>
                        <input name="travaux" placeholder="Description des travaux" value={formData.travaux} onChange={handleChange} className="p-2 border rounded w-64" />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">{declaration.Adresse} {declaration.Ville}</span>
                    </div>


                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">Maître d’ouvrage :</span>
                        <input name="maitreOuvrage" value={formData.maitreOuvrage} onChange={handleChange} className="p-2 border rounded w-64" />
                    </div>
                </div>

                <div class="my-10 border-t"></div>

                <div className="mt-6 text-gray-800 text-sm">
                    <p>
                        Je soussigné <input name="client" value={formData.client} onChange={handleChange} className="flex-1 p-2 border rounded" />, client, déclare avoir procédé le <input type="date" name="dateReception" value={formData.dateReception} onChange={handleChange} className="p-2 border rounded" /> à l'examen des travaux exécutés.
                    </p>
                    <p className="mt-2">Pour les travaux réalisés par l’entreprise ci-dessus, je déclare :</p>
                </div>

                <div className="flex flex-col gap-4 mt-4 text-sm">
                    <label>
                        <input type="radio" name="decision" value="acceptée" checked={formData.decision === 'acceptée'} onChange={handleChange} />
                        <span className="ml-2">La réception des travaux est prononcée sans réserves avec effet à la date du {formData.dateReception}</span>
                    </label>
                    <label>
                        <input type="radio" name="decision" value="réservée" checked={formData.decision === 'réservée'} onChange={handleChange} />
                        <span className="ml-2">La réception des travaux est prononcée avec les réserves consignées dans l’annexe ci-jointe à la date du {formData.dateReception}</span>
                    </label>
                    <label>
                        <input type="radio" name="decision" value="refusée" checked={formData.decision === 'refusée'} onChange={handleChange} />
                        <span className="ml-2">La réception des travaux est refusée pour les motifs consignés dans l’annexe ci-jointe à la date du {formData.dateReception}</span>
                    </label>
                </div>

                {(formData.decision === 'réservée' || formData.decision === 'refusée') && (
                    <p class="mt-4 text-sm">
                        L'entreprise et le Client conviennent que les travaux nécessités par les réserves ci-dessous seront exécutés
                        dans un délai de <input name="delai" value={formData.delai} onChange={handleChange} className="p-2 border rounded w-10" /> jours.
                    </p>
                )}

                <div className="mt-6 text-sm">
                    <p>
                        Fait à {'  '}
                        <input name="ville" placeholder="Ville" value={formData.ville} onChange={handleChange} className="inline-block p-1 border-gray-400 border-b w-40" />
                        {'  '}le {'  '}
                        <input type="date" name="dateFait" value={formData.dateFait} onChange={handleChange} className="p-2 border rounded" />

                    </p>
                    <p className="mt-1">

                    </p>
                </div>

                <div className="flex justify-between gap-8 mt-8">
                    <div className="flex flex-col items-center">
                        <p className="mb-1 text-sm">Signature de l’entreprise</p>
                        <SignaturePad ref={signatureEntrepriseRef} canvasProps={{ className: 'border w-full h-24 w-[300px] rounded' }} />
                    </div>
                    <div className="flex flex-col items-center">
                        <p className="mb-1 text-sm">Signature du client</p>
                        <SignaturePad ref={signatureClientRef} canvasProps={{ className: 'border w-full h-24 w-[300px] rounded' }} />
                    </div>
                </div>


            </div>

            <div className="bg-white shadow-md mx-auto px-20 py-24 w-full max-w-[1000px] ;t-12">

                {(formData.decision === 'réservée' || formData.decision === 'refusée') && (
                    <div className="mt-10">
                        <h2 className="mb-8 font-semibold text-xl text-center">
                            Listes des Réserves
                        </h2>

                        <div class="my-10 border-t"></div>

                        {reserves.map((res, i) => (
                            <div key={i} className="bg-white shadow-sm mb-6 p-4 border border-gray-300 rounded-md">
                                <div className="mb-3">
                                    <label className="block mb-1 font-medium text-sm">Réserve {i + 1}</label>
                                    <textarea
                                        value={res.text}
                                        placeholder={`Ex : Défaut d'étanchéité, fissure murale, etc.`}
                                        onChange={(e) => handleReserveChange(i, e.target.value)}
                                        className="p-2 border rounded focus:ring-2 focus:ring-blue-300 w-full text-sm resize-none"
                                        rows={3}
                                    />
                                </div>

                                <div className="mb-2">
                                    <label className="block mb-1 font-medium text-sm">Fichiers joints</label>
                                    <input
                                        type="file"
                                        accept="image/*,application/pdf"
                                        multiple
                                        onChange={(e) => handleFileUpload(i, e.target.files)}
                                        className="text-sm"
                                    />
                                </div>

                                {res.files?.length > 0 && (
                                    <div className="space-y-1 mt-2 text-gray-700 text-sm">
                                        {res.files.map((file, j) => (
                                            <div key={j} className="flex items-center gap-2">
                                                {file.type.includes('pdf') ? (
                                                    <span>📄</span>
                                                ) : (
                                                    <span>🖼️</span>
                                                )}
                                                <span className="truncate">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-4 text-right">
                                    <button
                                        onClick={() => removeReserve(i)}
                                        className="text-red-500 hover:text-red-700 text-sm"
                                    >
                                        Supprimer cette réserve
                                    </button>
                                </div>
                            </div>
                        ))}

                        <div className="text-right">
                            <button
                                onClick={addReserve}
                                className="bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded text-white text-sm transition-all duration-200"
                            >
                                + Ajouter une réserve
                            </button>
                        </div>
                    </div>
                )}

            </div>

        </div>
    );
};

export default GpaFormToPDF;